import { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings2, X } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import {
  GrilleMesures, GrilleRevenus, SerieMesures, ScenarioBudget,
  MOIS_LABELS, TYPES_MESURE_LABELS,
} from '../types';
import { BadgeTagRevenu, ChoixTagScenario } from '../components/TagScenarioRevenu';
import { HistoriqueRevenu } from '../components/HistoriqueRevenu';
import { ScenariosModal } from '../components/ScenariosModal';
import { CarteRetrocession } from '../components/CarteRetrocession';
import { SelectAnnee } from '../components/SelectAnnee';

/**
 * Décomptes mensuels des mesures en cours (v1 agrégée, saisie manuelle en
 * attendant le module Participants) : par programme (CCC/CTT) et type
 * (conventionnée/individualisée), effectifs d'un mois en avant. La série
 * conventionnée, comparée aux places du terme de convention (module
 * Contrats), détermine le risque de rétrocession et la sur-utilisation.
 */
export function MesuresPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [grille, setGrille] = useState<GrilleMesures | null>(null);
  // Grille Revenus : rétrocessions (croisement décomptes × facturé) et
  // quantités OAI (assurés par prestation, saisies dans Revenus)
  const [grilleRevenus, setGrilleRevenus] = useState<GrilleRevenus | null>(null);
  const [retroDepliee, setRetroDepliee] = useState<Set<number>>(new Set());
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [scenariosOuverts, setScenariosOuverts] = useState(false);
  const [edition, setEdition] = useState<{ serie: SerieMesures; mois: number } | null>(null);

  const charger = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    Promise.all([
      api.get<GrilleMesures>(`/mesures/grille?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`),
      // La rétrocession croise décomptes et revenus facturés — calculée par la grille Revenus
      api.get<GrilleRevenus>(`/revenus/grille?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`),
    ])
      .then(([gm, gr]) => {
        setGrille(gm);
        setGrilleRevenus(gr);
      })
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { charger(); }, [charger]);

  // Scénario affiché supprimé ou archivé : revenir au socle (le budget), et aux
  // décisions seules seulement si c'est le socle lui-même qui a disparu
  useEffect(() => {
    if (grille && scenarioId != null && !grille.scenarios.some((s) => s.id === scenarioId && !s.archive)) {
      setScenarioId(scenarioId === socleId ? null : socleId ?? null);
    }
  }, [grille, scenarioId, socleId]);

  const scenariosActifs = useMemo(
    () => (grille?.scenarios ?? []).filter((s) => !s.archive),
    [grille]
  );
  const variantes = useMemo(() => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]);
  const scenarioNom = scenarioId != null
    ? scenariosActifs.find((s) => s.id === scenarioId)?.nom ?? null
    : null;

  const moisCourant = annee === maintenant.getFullYear()
    ? maintenant.getMonth() + 1
    : annee < maintenant.getFullYear() ? 13 : 0;
  const classesColonne = (m: number) =>
    m < moisCourant ? ' col-passe' : m === moisCourant ? ' col-courant' : '';

  const referencePlaces = (programme: string) =>
    grille?.references.find((r) => r.programme === programme) ?? null;

  const retrocessions = grilleRevenus?.retrocessions ?? [];
  const fluxOai = (grilleRevenus?.flux ?? []).filter((f) => f.prestationId != null && f.actif);

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Mesures</h1>
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Décisions arrêtées seulement — les mois à venir n'y sont qu'un report">décidé seulement</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>variante · {scenarioNom}</span>}
        </div>
        <div className="flex gap-8 items-center">
          <SelectAnnee annee={annee} onChange={setAnnee} />
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      {retrocessions.length > 0 && (
        <div className="retro-row mb-16">
          {retrocessions.map((r) => (
            <CarteRetrocession
              key={r.contratId}
              retro={r}
              depliee={retroDepliee.has(r.contratId)}
              onBasculer={() => setRetroDepliee((prev) => {
                const s = new Set(prev);
                if (s.has(r.contratId)) s.delete(r.contratId); else s.add(r.contratId);
                return s;
              })}
            />
          ))}
        </div>
      )}

      <div className="toolbar">
        <div className="flex gap-8 items-center" title="Décisions prises seules, ou avec les hypothèses d'un scénario">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
            >
              Budget
            </button>
            <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
            >
              Variante
            </button>
            <button
              className={`selectable${scenarioId == null ? ' active' : ''}`}
              title="Décisions arrêtées seulement, sans aucune hypothèse — les mois à venir n'y sont qu'un report"
              onClick={() => setScenarioId(null)}
            >
              Décidé seulement
            </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button className="btn btn-icon" title="Gérer les scénarios" onClick={() => setScenariosOuverts(true)}>
            <Settings2 className="icon-sm" />
          </button>
        </div>
      </div>

      <div className="grille-container">
        <table className="table table-compact grille-salaires">
          <thead>
            <tr>
              <th className="cell-nom">Mesures en cours</th>
              {MOIS_LABELS.map((label, i) => (
                <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                  {label}
                  {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                </th>
              ))}
              <th className="cell-total">Décembre</th>
            </tr>
          </thead>
          <tbody>
            {['CCC', 'CTT'].map((programme) => {
              const ref = referencePlaces(programme);
              const series = (grille?.series ?? []).filter((s) => s.programme === programme);
              const serieConv = series.find((s) => s.type === 'conventionnee');
              const aDecomptes = serieConv?.mois.some((c) => c.nombre != null) ?? false;
              // Solde annuel en mois-places : cumul des décomptes en vigueur vs places × 12
              const cumul = aDecomptes
                ? serieConv!.mois.reduce((s, c) => s + (c.nombre ?? 0), 0)
                : null;
              const ecart = ref?.places != null && cumul != null
                ? Math.round((cumul - ref.places * 12) * 100) / 100
                : null;
              return [
                <tr key={`prog-${programme}`} className="ligne-groupe">
                  <td className="cell-nom">
                    Programme {programme}
                    {ref?.places != null && (
                      <span className="text-xs text-ink-3" style={{ marginLeft: 8 }}>
                        {ref.places} places = {ref.places * 12} mois-places/an{ref.tagTerme === 'projete' ? ' (terme projeté)' : ''}
                      </span>
                    )}
                    {ecart != null && ecart < 0 && (
                      <span className="badge badge-err badge-sm" style={{ marginLeft: 8 }}
                        title="Cumul annuel des décomptes sous la convention — rétrocession possible si des individualisées ont été vendues (voir Revenus)">
                        {ecart} mois-places
                      </span>
                    )}
                    {ecart != null && ecart > 0 && (
                      <span className="badge badge-warn badge-sm" style={{ marginLeft: 8 }}
                        title="Cumul annuel au-delà de la convention : pas de revenu supplémentaire, mais information clé pour les mesures individualisées">
                        sur-utilisation +{ecart} mois-places
                      </span>
                    )}
                  </td>
                  {Array.from({ length: 12 }, (_, i) => <td key={i} className={`cell-mois${classesColonne(i + 1)}`} />)}
                  <td className="cell-total" />
                </tr>,
                ...series.map((serie) => (
                  <tr key={`${programme}-${serie.type}`} className="ligne-employe">
                    <td className="cell-nom">
                      <span style={{ paddingLeft: 12 }}>{TYPES_MESURE_LABELS[serie.type] ?? serie.type}</span>
                      {serie.aProjections && <span className="badge badge-warn badge-sm" title="Comporte des valeurs projetées">proj.</span>}
                    </td>
                    {serie.mois.map((c) => (
                      <td
                        key={c.mois}
                        className={`cell-mois${classesColonne(c.mois)}${c.tag === 'projete' ? ' cell-projete' : ''}${c.nombre == null || c.parDefaut ? ' cell-vide' : ''}`}
                        title={[
                          `${programme} ${TYPES_MESURE_LABELS[serie.type]?.toLowerCase() ?? serie.type} — ${MOIS_LABELS[c.mois - 1]} ${annee}`,
                          c.parDefaut ? 'Prévision par défaut : places conventionnées (aucun décompte saisi)' : null,
                          c.changement ? `Changement (${c.changement.tag}) : ${c.changement.commentaire}` : null,
                          'Double-cliquer pour modifier',
                        ].filter(Boolean).join('\n')}
                        onDoubleClick={() => setEdition({ serie, mois: c.mois })}
                      >
                        {c.changement && <span className={`cell-flag cell-flag--${c.changement.tag === 'projete' ? 'projete' : 'definitif'}`} />}
                        <span className="cell-principal">{c.nombre != null ? `${c.parDefaut ? '(' : ''}${c.nombre}${c.parDefaut ? ')' : ''}` : '—'}</span>
                      </td>
                    ))}
                    <td className="cell-total">
                      <span className="cell-principal">{serie.mois[11].nombre ?? '—'}</span>
                    </td>
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </div>

      {fluxOai.length > 0 && (
        <>
          <div className="grille-container mt-16">
            <table className="table table-compact grille-salaires">
              <thead>
                <tr>
                  <th className="cell-nom">OAI — assurés par prestation</th>
                  {MOIS_LABELS.map((label, i) => (
                    <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                      {label}
                      {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                    </th>
                  ))}
                  <th className="cell-total">Décembre</th>
                </tr>
              </thead>
              <tbody>
                {fluxOai.map((f) => (
                  <tr key={f.id} className="ligne-employe">
                    {/*
                      Pas de vignette « proj. » ici : dans un tableau OAI tous
                      les mois à venir sont budgétés par construction, la
                      vignette serait allumée sur les quatre lignes en
                      permanence. La projection se lit cellule par cellule
                      (italique, drapeau, infobulle) et la note de bas de
                      tableau l'explique.
                    */}
                    <td className="cell-nom">{f.prestationLibelle ?? f.libelle}</td>
                    {f.mois.map((c) => {
                      const projete = c.tagQuantite === 'projete';
                      const reporte = !projete && c.mois > moisCourant && c.moisEffetQuantite != null &&
                        c.moisEffetQuantite.slice(0, 7) < `${annee}-${String(c.mois).padStart(2, '0')}`;
                      return (
                        <td
                          key={c.mois}
                          className={`cell-mois${classesColonne(c.mois)}${projete ? ' cell-projete' : ''}${(c.quantite ?? 0) === 0 || reporte ? ' cell-vide' : ''}`}
                          title={[
                            `${f.libelle} — ${MOIS_LABELS[c.mois - 1]} ${annee}`,
                            c.montantReel
                              ? `Facturé (réel du mois) : ${c.quantite ?? 0} assurés`
                              : projete
                                ? 'Budgété (projection de scénario)'
                                : reporte
                                  ? 'Prévision : report du dernier réel — sélectionner un scénario pour voir le budget'
                                  : null,
                            c.changementQuantite ? `Changement (${c.changementQuantite.tag}) : ${c.changementQuantite.commentaire}` : null,
                            'Saisie dans le module Revenus (double-clic pour y aller)',
                          ].filter(Boolean).join('\n')}
                          onDoubleClick={() => { window.location.hash = 'revenus'; }}
                        >
                          {c.changementQuantite && (
                            <span className={`cell-flag cell-flag--${c.changementQuantite.tag === 'projete' ? 'projete' : 'definitif'}`} />
                          )}
                          <span className="cell-principal">
                            {c.quantite != null ? `${reporte ? '(' : ''}${c.quantite}${reporte ? ')' : ''}` : '—'}
                          </span>
                        </td>
                      );
                    })}
                    <td className="cell-total">
                      <span className="cell-principal">{f.mois[11].quantite ?? '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-ink-3 mt-8">
            Assurés OAI par prestation de la convention cadre : nombre <strong>facturé</strong> pour les mois passés
            (justificatifs de remboursement), <strong>budgété</strong> pour les mois à venir (projections d'un
            scénario). Les valeurs entre parenthèses sont un simple report du dernier réel — sélectionner un scénario
            pour lire le budget. La saisie (et les montants facturés) se fait dans le module{' '}
            <a href="#revenus">Revenus</a>, vue Quantité.
          </p>
        </>
      )}

      <p className="text-xs text-ink-3 mt-8">
        Nombre de mesures en cours par mois. Le décompte réel du mois fait foi ; sans décompte, les projections d'un
        scénario s'appliquent (« dès tel mois ») et, à défaut, les mois à venir sont <strong>prévus aux places
        conventionnées</strong> — valeurs entre parenthèses (on ne reporte pas le dernier réel). Le cumul annuel en
        mois-places, comparé à places × 12 (terme du module Contrats), détermine le risque de rétrocession affiché
        dans Revenus ; la sur-utilisation ne rapporte rien de plus mais guide les mesures individualisées.
        <strong> Double-cliquer sur une cellule</strong> pour saisir un décompte.
      </p>

      {edition && grille && (
        <EditionMesure
          serie={edition.serie}
          mois={edition.mois}
          annee={annee}
          scenarios={grille.scenarios}
          scenarioCourant={scenarioId ?? null}
          onFermer={() => setEdition(null)}
          onModifie={() => { setEdition(null); charger(); }}
        />
      )}
      {scenariosOuverts && (
        <ScenariosModal onFermer={() => setScenariosOuverts(false)} onModifie={charger} />
      )}
    </div>
  );
}

function EditionMesure({ serie, mois, annee, scenarios, scenarioCourant, onFermer, onModifie }: {
  serie: SerieMesures;
  mois: number;
  annee: number;
  scenarios: ScenarioBudget[];
  scenarioCourant: number | null;
  onFermer: () => void;
  onModifie: () => void;
}) {
  const [moisEffet, setMoisEffet] = useState(`${annee}-${String(mois).padStart(2, '0')}`);
  const [nombre, setNombre] = useState(String(serie.mois[mois - 1]?.nombre ?? ''));
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(scenarioCourant);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Le décompte affiché est figé : il faut cliquer « Corriger » pour saisir.
  const [deverrouille, setDeverrouille] = useState(false);
  const celluleCourante = serie.mois[mois - 1];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const nomScenario = (id: number | null) => {
    if (id == null) return null;
    return scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;
  };

  const valide =
    /^\d{4}-\d{2}$/.test(moisEffet) &&
    nombre.trim() !== '' && Number(nombre) >= 0 &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null);

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.post('/mesures/evenements', {
        programme: serie.programme, type: serie.type,
        moisEffet, nombre: Number(nombre),
        ...(tag === 'projete' ? { tag, scenarioId } : { tag }),
        commentaire: commentaire.trim(),
      });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  async function supprimer(id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/mesures/evenements/${id}`, { commentaire: c.trim() });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {serie.programme} — {TYPES_MESURE_LABELS[serie.type] ?? serie.type}
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!deverrouille ? (
            <div className="formulaire-changement mb-16">
              <div className="formulaire-changement__titre">
                Décompte {MOIS_LABELS[mois - 1]} {annee}
              </div>
              <div className="flex items-center gap-8">
                <span className="text-sm">
                  <strong>{celluleCourante?.nombre ?? '—'}</strong> mesures en cours
                </span>
                {celluleCourante?.parDefaut && (
                  <span className="badge badge-sm" title="Aucun décompte saisi — prévision aux places conventionnées">
                    par défaut (places conventionnées)
                  </span>
                )}
                {celluleCourante?.tag && (
                  <BadgeTagRevenu tag={celluleCourante.tag}
                    scenarioNom={scenarioCourant != null ? scenarios.find((s) => s.id === scenarioCourant)?.nom ?? null : null} />
                )}
              </div>
              <div className="flex items-center justify-between mt-8">
                <span className="text-xs text-ink-3">Valeur figée — corriger crée un nouvel événement tracé.</span>
                <button className="btn btn-accent btn-sm" onClick={() => setDeverrouille(true)}>Corriger ✎</button>
              </div>
            </div>
          ) : (
          <div className="formulaire-changement mb-16">
            <div className="formulaire-changement__titre">Décompte à partir d'un mois donné</div>
            <div className="flex gap-12 flex-wrap items-end">
              <div className="form-group">
                <label className="form-label">À partir de</label>
                <input type="month" className="form-input" value={moisEffet} onChange={(e) => setMoisEffet(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Mesures en cours</label>
                <input type="number" className="form-input" style={{ width: 100 }} min={0} step={0.5} autoFocus
                  value={nombre} onChange={(e) => setNombre(e.target.value)} onFocus={(e) => e.target.select()} />
              </div>
              <div className="form-group">
                <label className="form-label">Statut</label>
                <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
                  onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
              </div>
            </div>
            <div className="form-group mt-8">
              <label className="form-label">Commentaire (obligatoire)</label>
              <input className="form-input w-full" placeholder="Source du décompte…"
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            <div className="flex justify-end mt-8">
              <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
          )}

          {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

          <HistoriqueRevenu
            titre="Historique"
            lignes={serie.evenements.map((e) => ({
              id: e.id, mois: e.mois_effet, valeur: `${e.nombre} mesures`,
              tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
            }))}
            onSupprimer={supprimer}
          />
        </div>
      </div>
    </div>
  );
}
